// Tasks list (§11 `/tasks`): all schedules with state filters. Columns surface schedule, engine
// (tick vs do_alarm), next run, and lifecycle state. Filter by service/status via query params.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtRelative } from "../lib/format";
import { Badge, Empty, ErrorBox, Spinner } from "../components/ui";

const STATE_FILTERS = ["all", "enabled", "paused", "disabled"] as const;
type StateFilter = (typeof STATE_FILTERS)[number];

export function TasksPage() {
  const [params, setParams] = useSearchParams();
  const serviceId = params.get("serviceId") ?? "";
  const [state, setState] = useState<StateFilter>("all");

  const query: Record<string, string | undefined> = {};
  if (serviceId) query.serviceId = serviceId;
  const { data, error, loading } = useAsync(() => Api.listTasks(query), [serviceId]);

  const tasks = (data?.data ?? []).filter((t) => {
    if (state === "all") return true;
    if (state === "paused") return t.paused;
    if (state === "enabled") return t.enabled && !t.paused && !t.deletedAt;
    if (state === "disabled") return !t.enabled && !t.deletedAt;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Tasks</h1>
        <Link className="btn btn-primary" to="/tasks/new">
          New task
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATE_FILTERS.map((f) => (
          <button
            key={f}
            className={`btn ${state === f ? "btn-primary" : ""}`}
            onClick={() => setState(f)}
          >
            {f}
          </button>
        ))}
        {serviceId && (
          <button
            className="btn"
            onClick={() => {
              params.delete("serviceId");
              setParams(params);
            }}
          >
            Clear service filter ({serviceId})
          </button>
        )}
      </div>

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} />
      ) : tasks.length === 0 ? (
        <Empty>No tasks match.</Empty>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full bg-surface">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Service</th>
                <th className="th">Target</th>
                <th className="th">Schedule</th>
                <th className="th">Engine</th>
                <th className="th">Next run</th>
                <th className="th">State</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} className="hover:bg-bg/50">
                  <td className="td">
                    <Link className="mono text-primary hover:underline" to={`/tasks/${t.id}`}>
                      {t.id}
                    </Link>
                  </td>
                  <td className="td mono text-xs text-muted">{t.serviceId}</td>
                  <td className="td">
                    <Badge>{t.targetType}</Badge>
                  </td>
                  <td className="td mono text-xs">
                    {t.scheduleKind === "cron"
                      ? t.cron
                      : t.scheduleKind === "interval"
                        ? `every ${t.intervalSeconds}s`
                        : "one-shot"}
                  </td>
                  <td className="td">
                    <Badge tone={t.scheduleEngine === "do_alarm" ? "warn" : "muted"}>{t.scheduleEngine}</Badge>
                  </td>
                  <td className="td mono text-xs">{fmtRelative(t.nextRunAt)}</td>
                  <td className="td">
                    {t.deletedAt ? (
                      <Badge tone="danger">deleted</Badge>
                    ) : t.paused ? (
                      <Badge tone="warn">paused</Badge>
                    ) : t.enabled ? (
                      <Badge tone="ok">enabled</Badge>
                    ) : (
                      <Badge tone="danger">disabled</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}